import React, {useRef, useEffect} from 'react'
import {InfiniteLoader, AutoSizer, List as VirtualList, CellMeasurerCache, CellMeasurer} from 'react-virtualized'
import Spinner from './Spinner'
import {Optional} from 'data/types'

type ListItemRenderer<T> = (props: {index: number; data: T}) => React.ReactNode

type ListProps<T> = {
  items: T[]
  children: ListItemRenderer<T>
  estimatedRowHeight?: number
  loadMore?: Optional<() => Promise<any>>
}

export default function List<T>(props: ListProps<T>) {
  const layoutCacheRef = useRef(new CellMeasurerCache({defaultHeight: props.estimatedRowHeight ?? 50, fixedWidth: true}))
  const mostRecentWidth = useRef<number>(0)
  const listRef = useRef<any>(React.createRef())

  const resize = () => {
    layoutCacheRef.current.clearAll()
    if (listRef.current) {
      listRef.current.recomputeRowHeights()
    }
  }

  useEffect(() => {
    resize()
  }, [props.items, props.items.length])

  const totalItems = props.items.length + (props.loadMore ? 1 : 0)

  return (
    <InfiniteLoader
      isRowLoaded={(input: {index: number}) => input.index < props.items.length}
      rowCount={totalItems}
      loadMoreRows={async () => {
        await props.loadMore?.()
      }}
    >
      {({onRowsRendered, registerChild}) => {
        return (
          <AutoSizer>
            {({width, height}) => {
              if (mostRecentWidth.current !== width) {
                setTimeout(resize, 0)
              }
              mostRecentWidth.current = width
              return (
                <VirtualList
                  style={{outline: 'none'}}
                  deferredMeasurementCache={layoutCacheRef.current}
                  rowHeight={layoutCacheRef.current.rowHeight}
                  ref={(ref) => {
                    if (!listRef.current) {
                      registerChild(ref)
                    }
                    listRef.current = ref
                  }}
                  height={height}
                  overscanRowCount={1}
                  width={width}
                  rowCount={totalItems}
                  onRowsRendered={onRowsRendered}
                  rowRenderer={(p) => {
                    let content: React.ReactNode

                    if (p.index < props.items.length) {
                      content = (
                        <div style={p.style}>
                          {props.children({
                            index: p.index,
                            data: props.items[p.index],
                          })}
                        </div>
                      )
                    } else {
                      content = (
                        <div style={p.style} className="py-8 flex flex-row justify-center">
                          <Spinner />
                        </div>
                      )
                    }
                    return (
                      <CellMeasurer
                        key={p.key}
                        width={mostRecentWidth.current}
                        rowIndex={p.index}
                        parent={p.parent}
                        columnIndex={0}
                        cache={layoutCacheRef.current}
                      >
                        {content}
                      </CellMeasurer>
                    )
                  }}
                />
              )
            }}
          </AutoSizer>
        )
      }}
    </InfiniteLoader>
  )
}
